import { useEffect, useState } from "react";
import { notify } from "@/components/ui/dialogs";
import { get, post, put, del, can } from "@/utils/service";
import { apiMessage, label } from "@/utils/api";
import { Panel, Field, input, primary, secondary, danger } from "./shared";
import AppSelect from "@/components/ui/app-select";

interface TaxRate { id: string; name: string; rate: string; description: string | null; is_default: boolean; is_active: boolean }
interface Method { id: string; name: string; type: string; instructions: string | null; is_active: boolean }

const METHOD_TYPES = ["bank_transfer", "mobile_money", "cash", "card", "cheque", "online", "other"];

/** Taxes applied to quote and invoice lines, and the ways you accept payment. */
export default function FinanceSetup() {
  const editable = can("invoices.configure");
  const [rates, setRates] = useState<TaxRate[]>([]);
  const [methods, setMethods] = useState<Method[]>([]);
  const [rate, setRate] = useState<Partial<TaxRate> | null>(null);
  const [method, setMethod] = useState<Partial<Method> | null>(null);

  const load = () => Promise.all([
    get<{ data: { tax_rates: TaxRate[] } }>("/tax-rates?include_inactive=1").then((r) => setRates(r.data.tax_rates)),
    get<{ data: { payment_methods: Method[] } }>("/payment-methods?include_inactive=1").then((r) => setMethods(r.data.payment_methods)),
  ]).catch((e) => notify(apiMessage(e, "Could not load finance settings.")));
  useEffect(() => { load(); }, []);

  const saveRate = async () => {
    if (!rate) return;
    try {
      const body = { name: rate.name, rate: rate.rate, description: rate.description || null, is_default: !!rate.is_default, is_active: rate.is_active ?? true };
      if (rate.id) await put(`/tax-rates/${rate.id}`, body); else await post("/tax-rates", body);
      notify("Tax rate saved. Documents already issued keep the rate they used.");
      setRate(null);
      load();
    } catch (e) { notify(apiMessage(e, "Could not save the tax rate.")); }
  };
  const removeRate = async (r: TaxRate) => {
    try { const res = await del<{ message: string }>(`/tax-rates/${r.id}`); notify(res.message); load(); }
    catch (e) { notify(apiMessage(e, "Could not remove the tax rate.")); }
  };
  const saveMethod = async () => {
    if (!method) return;
    try {
      const body = { name: method.name, type: method.type ?? "bank_transfer", instructions: method.instructions || null, is_active: method.is_active ?? true };
      if (method.id) await put(`/payment-methods/${method.id}`, body); else await post("/payment-methods", body);
      notify("Payment method saved");
      setMethod(null);
      load();
    } catch (e) { notify(apiMessage(e, "Could not save the payment method.")); }
  };
  const removeMethod = async (m: Method) => {
    try { const res = await del<{ message: string }>(`/payment-methods/${m.id}`); notify(res.message); load(); }
    catch (e) { notify(apiMessage(e, "Could not remove the payment method.")); }
  };

  return (
    <div className="space-y-6">
      <Panel title="Tax Rates" description="Added on top of each line's discounted amount" actions={editable && !rate && <button onClick={() => setRate({ is_active: true })} className={secondary}>New tax rate</button>}>
        {rate && (
          <div className="mb-6 grid gap-4 rounded-lg border border-gray-200 p-4 md:grid-cols-3">
            <Field label="Name"><input className={input} value={rate.name ?? ""} onChange={(e) => setRate({ ...rate, name: e.target.value })} placeholder="e.g. VAT" /></Field>
            <Field label="Rate (%)"><input type="number" min="0" max="100" step="0.01" className={input} value={rate.rate ?? ""} onChange={(e) => setRate({ ...rate, rate: e.target.value })} /></Field>
            <Field label="Description"><input className={input} value={rate.description ?? ""} onChange={(e) => setRate({ ...rate, description: e.target.value })} /></Field>
            <label className="flex items-center gap-2 text-xs text-gray-700"><input type="checkbox" className="accent-primary" checked={!!rate.is_default} onChange={(e) => setRate({ ...rate, is_default: e.target.checked })} />Default for new lines</label>
            <label className="flex items-center gap-2 text-xs text-gray-700"><input type="checkbox" className="accent-primary" checked={rate.is_active ?? true} onChange={(e) => setRate({ ...rate, is_active: e.target.checked })} />Active</label>
            <div className="flex justify-end gap-2"><button onClick={() => setRate(null)} className={secondary}>Cancel</button><button onClick={saveRate} disabled={!rate.name || rate.rate === undefined || rate.rate === ""} className={primary}>Save</button></div>
          </div>
        )}
        {rates.length === 0 ? <p className="text-xs text-gray-500">No tax rates yet. Lines are tax-free until you add one.</p> : (
          <div className="divide-y divide-gray-100">
            {rates.map((r) => (
              <div key={r.id} className={`flex items-center justify-between gap-3 py-3 ${r.is_active ? "" : "opacity-50"}`}>
                <p className="text-xs text-gray-900"><span className="font-semibold">{r.name}</span> · {Number(r.rate)}%{r.is_default && <span className="ml-2 rounded-full bg-primary/10 px-2 py-0.5 text-[11px] text-primary">Default</span>}{!r.is_active && " · inactive"}</p>
                {editable && <div className="flex gap-2"><button className={secondary} onClick={() => setRate(r)}>Edit</button><button className={danger} onClick={() => removeRate(r)}>Remove</button></div>}
              </div>
            ))}
          </div>
        )}
      </Panel>

      <Panel title="Payment Methods" description="How customers pay you; instructions can include bank or mobile money details" actions={editable && !method && <button onClick={() => setMethod({ type: "bank_transfer", is_active: true })} className={secondary}>New method</button>}>
        {method && (
          <div className="mb-6 grid gap-4 rounded-lg border border-gray-200 p-4 md:grid-cols-2">
            <Field label="Name"><input className={input} value={method.name ?? ""} onChange={(e) => setMethod({ ...method, name: e.target.value })} /></Field>
            <Field label="Type"><AppSelect className={input} value={method.type} onChange={(e) => setMethod({ ...method, type: e.target.value })}>{METHOD_TYPES.map((t) => <option key={t} value={t}>{label(t)}</option>)}</AppSelect></Field>
            <div className="md:col-span-2"><Field label="Instructions"><textarea rows={3} className={input} value={method.instructions ?? ""} onChange={(e) => setMethod({ ...method, instructions: e.target.value })} /></Field></div>
            <label className="flex items-center gap-2 text-xs text-gray-700"><input type="checkbox" className="accent-primary" checked={method.is_active ?? true} onChange={(e) => setMethod({ ...method, is_active: e.target.checked })} />Active</label>
            <div className="flex justify-end gap-2"><button onClick={() => setMethod(null)} className={secondary}>Cancel</button><button onClick={saveMethod} disabled={!method.name} className={primary}>Save</button></div>
          </div>
        )}
        <div className="divide-y divide-gray-100">
          {methods.map((m) => (
            <div key={m.id} className={`flex items-center justify-between gap-3 py-3 ${m.is_active ? "" : "opacity-50"}`}>
              <div><p className="text-xs font-semibold text-gray-900">{m.name}</p><p className="text-xs text-gray-500">{label(m.type)}{m.instructions ? ` · ${m.instructions}` : ""}</p></div>
              {editable && <div className="flex gap-2"><button className={secondary} onClick={() => setMethod(m)}>Edit</button><button className={danger} onClick={() => removeMethod(m)}>Remove</button></div>}
            </div>
          ))}
        </div>
      </Panel>
    </div>
  );
}
